import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { HomePage } from './pages/HomePage';
import { ProductsPage } from './pages/ProductsPage';
import { GradingPage } from './pages/GradingPage';
import { RefurbishmentPage } from './pages/RefurbishmentPage';
import { ExportPage } from './pages/ExportPage';
import { AboutPage } from './pages/AboutPage';
import { ContactPage } from './pages/ContactPage';
import { FAQsPage } from './pages/FAQsPage';
import { RequestPage } from './pages/RequestPage';
import { AdminLoginPage } from './pages/admin/AdminLoginPage';
import { AdminLayout } from './pages/admin/AdminLayout';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { AdminProducts } from './pages/admin/AdminProducts';
import { AdminImport } from './pages/admin/AdminImport';
import { AdminOrders, AdminOrderDetail } from './pages/admin/AdminOrders';
import { AdminSettings } from './pages/admin/AdminSettings';
import { AdminLeads, AdminLeadDetail } from './pages/admin/AdminLeads';
import { AdminBuyers, AdminBuyerDetail } from './pages/admin/AdminBuyers';
import WholesaleRegisterPage from './pages/WholesaleRegisterPage';
import BuyerLoginPage from './pages/buyer/BuyerLoginPage';
import SetPasswordPage from './pages/buyer/SetPasswordPage';
import ForgotPasswordPage from './pages/buyer/ForgotPasswordPage';
import ResetPasswordPage from './pages/buyer/ResetPasswordPage';
import BuyerDashboard from './pages/buyer/BuyerDashboard';
import BuyerCartPage from './pages/buyer/BuyerCartPage';
import BuyerOrderDetail from './pages/buyer/BuyerOrderDetail';
import SavedListsPage from './pages/buyer/SavedListsPage';
import QuoteListPage from './pages/QuoteListPage';
import { BulkOrderBuilderPage } from './pages/BulkOrderBuilderPage';
import { AdminQuotes, AdminQuoteDetail } from './pages/admin/AdminQuotes';
import { AdminContacts } from './pages/admin/AdminContacts';

// Recovered admin modules
import { AdminAnalytics } from './pages/admin/AdminAnalytics';
import { AdminBulkLots } from './pages/admin/AdminBulkLots';
import { AdminCampaigns } from './pages/admin/AdminCampaigns';
import { AdminCampaignDetail } from './pages/admin/AdminCampaignDetail';
import { AdminCampaignEditor } from './pages/admin/AdminCampaignEditor';
import { AdminCustomers } from './pages/admin/AdminCustomers';
import { AdminEmailAnalytics } from './pages/admin/AdminEmailAnalytics';
import { AdminGallery } from './pages/admin/AdminGallery';
import { AdminProspects } from './pages/admin/AdminProspects';
import { AdminRfqCampaigns } from './pages/admin/AdminRfqCampaigns';
import { AdminSenderProfiles } from './pages/admin/AdminSenderProfiles';
import { AdminSourcingRequests } from './pages/admin/AdminSourcingRequests';
import { AdminSubscribers } from './pages/admin/AdminSubscribers';
import { AdminSupplierQuotes } from './pages/admin/AdminSupplierQuotes';
import { AdminSuppliers } from './pages/admin/AdminSuppliers';

import '@/App.css';

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Public Layout wrapper
const PublicLayout = ({ children }) => (
  <>
    <Header />
    <main>{children}</main>
    <Footer />
  </>
);

// Buyer route guard
const BuyerRoute = ({ children }) => {
  const { isBuyer } = useApp();
  if (!isBuyer) return <Navigate to="/buyer/login" replace />;
  return <PublicLayout>{children}</PublicLayout>;
};

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <ScrollToTop />
        <div className="min-h-screen bg-[#050505]">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
            <Route path="/products" element={<PublicLayout><ProductsPage /></PublicLayout>} />
            <Route path="/grading" element={<PublicLayout><GradingPage /></PublicLayout>} />
            <Route path="/refurbishment" element={<PublicLayout><RefurbishmentPage /></PublicLayout>} />
            <Route path="/export" element={<PublicLayout><ExportPage /></PublicLayout>} />
            <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
            <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
            <Route path="/faqs" element={<PublicLayout><FAQsPage /></PublicLayout>} />
            <Route path="/request" element={<PublicLayout><RequestPage /></PublicLayout>} />
            <Route path="/bulk-order" element={<PublicLayout><BulkOrderBuilderPage /></PublicLayout>} />
            <Route path="/quote" element={<PublicLayout><QuoteListPage /></PublicLayout>} />
            <Route path="/wholesale-register" element={<PublicLayout><WholesaleRegisterPage /></PublicLayout>} />
            
            {/* Buyer Auth Routes */}
            <Route path="/buyer/login" element={<BuyerLoginPage />} />
            <Route path="/set-password" element={<SetPasswordPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Buyer Protected Routes */}
            <Route path="/buyer" element={<BuyerRoute><BuyerDashboard /></BuyerRoute>} />
            <Route path="/buyer/cart" element={<BuyerRoute><BuyerCartPage /></BuyerRoute>} />
            <Route path="/buyer/saved-lists" element={<BuyerRoute><SavedListsPage /></BuyerRoute>} />
            <Route path="/buyer/orders/:id" element={<BuyerRoute><BuyerOrderDetail /></BuyerRoute>} />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="import" element={<AdminImport />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="orders/:id" element={<AdminOrderDetail />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="leads/:id" element={<AdminLeadDetail />} />
              <Route path="buyers" element={<AdminBuyers />} />
              <Route path="buyers/:id" element={<AdminBuyerDetail />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="quotes" element={<AdminQuotes />} />
              <Route path="quotes/:quoteId" element={<AdminQuoteDetail />} />
              <Route path="contacts" element={<AdminContacts />} />
              {/* Recovered admin modules */}
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="bulk-lots" element={<AdminBulkLots />} />
              <Route path="gallery" element={<AdminGallery />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="sourcing-requests" element={<AdminSourcingRequests />} />
              <Route path="prospects" element={<AdminProspects />} />
              <Route path="campaigns" element={<AdminCampaigns />} />
              <Route path="campaigns/:id" element={<AdminCampaignDetail />} />
              <Route path="campaigns/:id/edit" element={<AdminCampaignEditor />} />
              <Route path="email-analytics" element={<AdminEmailAnalytics />} />
              <Route path="sender-profiles" element={<AdminSenderProfiles />} />
              <Route path="subscribers" element={<AdminSubscribers />} />
              <Route path="suppliers" element={<AdminSuppliers />} />
              <Route path="rfq-campaigns" element={<AdminRfqCampaigns />} />
              <Route path="supplier-quotes" element={<AdminSupplierQuotes />} />
            </Route>
          </Routes>
        </div>
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
